import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Container,
  Row,
  Table,
  Input,
  Col,
} from "reactstrap";
import Header from "components/Headers/Header";
import { useTablePaging, PAGE_SIZE_OPTIONS } from "../../stores/pagingStore";
import { api } from "../../config/middleware";
import { API_GETCUSTOMERS } from "../../config/api";
import { useSessionStore } from "../../stores/sessionStore";
import LoadingSpinner from "components/LoadingSpinner";

const TABLE_KEY = "customer";

const Customer = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const orgId = useSessionStore((s) => s.profile?.orgId);
  const role = (
    user?.role ||
    localStorage.getItem("role") ||
    "user"
  ).toLowerCase();

  const { page, pageSize, setPage, setPageSize } = useTablePaging(TABLE_KEY);

  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [keyword, setKeyword] = useState("");

  // ✅ responsive: จอเล็กใช้แบบเดิม
  const [isSmall, setIsSmall] = useState(window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsSmall(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const fetchCustomers = async () => {
    if (!orgId) return;
    setIsLoading(true);
    try {
      const res = await api.get(API_GETCUSTOMERS, {
        params: { ORGID: orgId, CMCODE: "" },
      });
      const data = Array.isArray(res?.data?.data) ? res.data.data : [];
      setRows(data);
      setPage(TABLE_KEY, 1);
    } catch (err) {
      console.error("fetchCustomers error:", err);
      alert("โหลดข้อมูลลูกค้าไม่สำเร็จ");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const norm = (v) => (v ?? "").toString().toLowerCase().trim();

  const filteredRows = useMemo(() => {
    const k = norm(keyword);
    if (!k) return rows;
    return rows.filter((c) => {
      const phones = [
        c.cmContactMobile,
        c.cmContactMobile2,
        c.cmMobile,
        c.cmBillMobile,
      ]
        .filter(Boolean)
        .join(" ");
      const emails = [c.cmEmail, c.cmBillEmail].filter(Boolean).join(" ");
      return (
        norm(c.cmCode).includes(k) ||
        norm(c.cmName).includes(k) ||
        norm(c.cmType).includes(k) ||
        norm(phones).includes(k) ||
        norm(emails).includes(k)
      );
    });
  }, [rows, keyword]);

  const total = filteredRows.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(page, pageCount);
  const startIdx = (current - 1) * pageSize;
  const displayRows = filteredRows.slice(startIdx, startIdx + pageSize);

  const filteredTotal = filteredRows.length;
  const filteredPageCount = Math.max(
    1,
    Math.ceil(filteredRows.length / pageSize),
  );

  const buildPages = (p, max) => {
    const arr = [];
    const start = Math.max(1, p - 1);
    const end = Math.min(max, p + 1);
    if (start > 1) arr.push(1);
    for (let i = start; i <= end; i++) arr.push(i);
    if (end < max) arr.push(max);
    return [...new Set(arr)];
  };
  const pages = buildPages(current, pageCount);

  const handleAddCustomer = () => navigate("/add-customer");

  const goDetail = (row) => {
    navigate("/edit-customer", { state: { row } });
  };

  const onChangePageSize = (e) =>
    setPageSize(TABLE_KEY, Number(e.target.value));

  useEffect(() => {
    setPage(TABLE_KEY, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword]);

  if (isLoading) return <LoadingSpinner fullPage />;

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        <Row>
          <div className="col">
            <Card className="shadow bg-secondary">
              <CardHeader className="border-0 d-flex align-items-center justify-content-between">
                <h6 className="mb-0">ข้อมูลลูกค้า</h6>
                <Button color="success" onClick={handleAddCustomer}>
                  เพิ่มข้อมูลลูกค้า
                </Button>
              </CardHeader>

              <CardBody>
                {/* Toolbar */}
                <div className="mb-3">
                  <Row className="align-items-center g-0">
                    <Col
                      xs="12"
                      md="6"
                      className="d-flex align-items-center flex-wrap"
                      style={{ gap: 8 }}
                    >
                      <Input
                        type="select"
                        value={pageSize}
                        onChange={onChangePageSize}
                        style={{ width: 96 }}
                      >
                        {PAGE_SIZE_OPTIONS.map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </Input>
                      <span className="text-muted">แถว/หน้า</span>
                    </Col>

                    <Col
                      xs="12"
                      md="6"
                      className="mt-2 mt-md-0 d-flex justify-content-md-end"
                    >
                      <Input
                        type="text"
                        placeholder="ค้นหา รหัสลูกค้า/ชื่อลูกค้า/เบอร์โทร"
                        value={keyword}
                        onChange={(e) => setKeyword(e.target.value)}
                        className="table-modern-search w-100 w-md-auto"
                        style={{ maxWidth: 360, minWidth: 280 }}
                      />
                    </Col>
                  </Row>
                </div>

                {/* ✅ TABLE: เล็ก=เดิม, กลาง/ใหญ่=แบบรูป */}
                {isSmall ? (
                  <div className="table-responsive table-modern-wrap">
                    <Table responsive hover className="table table-modern mb-0">
                      <thead className="thead-colored">
                        <tr>
                          <th className="text-start">รหัสลูกค้า</th>
                          <th className="text-start">ชื่อลูกค้า</th>
                          <th className="text-start">ประเภท</th>
                          <th className="text-start">เบอร์โทร</th>
                          <th className="text-start">อีเมล</th>
                          <th
                            className="text-start"
                            style={{ width: "140px", paddingRight: "50px" }}
                          >
                            จัดการ
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {displayRows.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="text-center text-muted">
                              ไม่พบข้อมูลลูกค้า
                            </td>
                          </tr>
                        ) : (
                          displayRows.map((c, i) => (
                            <tr key={c.cmCode || `${c.cmName}-${i}`}>
                              <td data-th="รหัสลูกค้า">{c.cmCode}</td>
                              <td data-th="ชื่อลูกค้า">{c.cmName}</td>
                              <td data-th="ประเภท">{c.cmType || "-"}</td>
                              <td data-th="เบอร์โทร">
                                {c.cmContactMobile ||
                                  c.cmContactMobile2 ||
                                  c.cmMobile ||
                                  c.cmBillMobile ||
                                  "-"}
                              </td>
                              <td data-th="อีเมล">
                                {c.cmEmail || c.cmBillEmail || "-"}
                              </td>
                              <td className="text-start" data-th="จัดการ">
                                <div className="d-flex gap-2">
                                  <Button
                                    color="warning"
                                    size="sm"
                                    onClick={() => goDetail(c)}
                                  >
                                    แก้ไข
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <div className="zi-table-wrap">
                    <Table responsive hover className="zi-table mb-0">
                      <thead>
                        <tr>
                          <th className="text-start" style={{ width: 130 }}>
                            รหัสลูกค้า
                          </th>
                          <th className="text-start" style={{ width: 200 }}>
                            ชื่อลูกค้า
                          </th>
                          <th className="text-start" style={{ width: 160 }}>
                            ประเภท
                          </th>
                          <th className="text-start" style={{ width: 160 }}>
                            เบอร์โทร
                          </th>
                          <th className="text-start" style={{ width: 160 }}>
                            อีเมล
                          </th>
                          <th className="text-end" style={{ width: 160 }}>
                            การจัดการ
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {displayRows.length === 0 ? (
                          <tr>
                            <td
                              colSpan={6}
                              className="text-center text-muted py-4"
                            >
                              ไม่พบข้อมูลลูกค้า
                            </td>
                          </tr>
                        ) : (
                          displayRows.map((c, i) => {
                            const phone =
                              c.cmContactMobile ||
                              c.cmContactMobile2 ||
                              c.cmMobile ||
                              c.cmBillMobile ||
                              "-";
                            const email = c.cmEmail || c.cmBillEmail || "-";

                            return (
                              <tr key={c.cmCode || `${c.cmName}-${i}`}>
                                <td className="text-start">{c.cmCode}</td>
                                <td className="text-start fw-600">
                                  {c.cmName}
                                </td>
                                <td className="text-start">
                                  {c.cmType || "-"}
                                </td>
                                <td className="text-start">{phone}</td>
                                <td className="text-start">{email}</td>
                                <td className="text-end">
                                  <div className="d-inline-flex gap-2">
                                    <Button
                                      color="warning"
                                      size="sm"
                                      onClick={() => goDetail(c)}
                                    >
                                      แก้ไข
                                    </Button>
                                    <Button
                                      color="success"
                                      size="sm"
                                      onClick={() =>
                                        navigate("/product-customer", {
                                          state: { customer: c },
                                        })
                                      }
                                    >
                                      เพิ่มสินค้า
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </Table>
                  </div>
                )}

                {/* summary */}
                <div className="d-flex justify-content-end mt-2">
                  <div className="text-muted small">
                    แสดง {total === 0 ? 0 : startIdx + 1}–
                    {Math.min(startIdx + displayRows.length, total)} จากทั้งหมด{" "}
                    {total} รายการ
                  </div>
                </div>

                {/* ✅ CSS ของ table แบบรูป (มีผลเฉพาะ desktop/tablet) */}
                <style>{`
                  .zi-table-wrap{
                    background:#fff;
                    border-radius:10px;
                    border:1px solid rgba(0,0,0,.06);
                    overflow:hidden;
                  }
                  .zi-table{
                    margin:0;
                  }
                  .zi-table thead th{
                    background:#f7fafc;
                    color:#0f172a;
                    font-weight:700;
                    font-size:14px;
                    border-bottom:1px solid rgba(0,0,0,.06);
                    padding:14px 16px;
                    white-space:nowrap;
                  }
                  .zi-table tbody td{
                    padding:14px 16px;
                    border-top:1px solid rgba(0,0,0,.06);
                    vertical-align:middle;
                    font-size:14px;
                    color:#0f172a;
                  }
                  .zi-table tbody tr:hover{
                    background:#f9fbff;
                  }
                  .fw-600{ font-weight:600; }
                `}</style>
              </CardBody>

              {/* Pagination */}
              {filteredTotal > 0 && (
                <CardFooter
                  className="py-4"
                  style={{ position: "relative", zIndex: 10 }}
                >
                  <div className="paging-wrap">
                    <button
                      className="paging-btn icon"
                      onClick={() => setPage(TABLE_KEY, Math.max(1, page - 1))}
                      disabled={page === 1}
                    >
                      ‹
                    </button>

                    {pages.map((p) => (
                      <button
                        key={p}
                        className={`paging-btn ${page === p ? "active" : ""}`}
                        onClick={() => setPage(TABLE_KEY, p)}
                      >
                        {p}
                      </button>
                    ))}

                    <button
                      className="paging-btn icon"
                      onClick={() =>
                        setPage(
                          TABLE_KEY,
                          Math.min(filteredPageCount, page + 1),
                        )
                      }
                      disabled={page === filteredPageCount}
                      aria-label="Next page"
                    >
                      ›
                    </button>
                  </div>
                </CardFooter>
              )}
            </Card>
          </div>
        </Row>
      </Container>
    </>
  );
};

export default Customer;
